import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Spinner, Alert
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";


export default function ModifierFournisseur() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        raison_sociale: '',
        adresse: '',
        telephone: '',
        email: '',
        ice: '',
        patente: '',
        rc: '',
        identifiant_fiscal: '',
        cnss: '',
        conditions_reglement: ''
    });
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState(false);

    useEffect(() => {
        fetchFournisseur();
    }, [id]);

    const fetchFournisseur = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setFormData(result.data);
        } catch (err) {
            setSubmitError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        
        if (errors[name]) {
            setErrors({ ...errors, [name]: null });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.raison_sociale) newErrors.raison_sociale = "La raison sociale est requise";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setSaving(true);
        setSubmitError('');
        setSubmitSuccess(false);

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(formData)
                }
            );

            const result = await response.json();

            if (result.success) {
                setSubmitSuccess(true);
                setTimeout(() => {
                    navigate('/achats/fournisseurs');
                }, 1500);
            } else {
                setSubmitError(result.message || "Erreur lors de la modification");
            }
        } catch (err) {
            setSubmitError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des informations...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-pencil-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Modifier {formData.raison_sociale}</h4>
                                    <p className="text-muted small mb-0">Code: {formData.code_fournisseur}</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate(-1)}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Alertes */}
                    {submitSuccess && (
                        <Alert variant="success" onClose={() => setSubmitSuccess(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            Fournisseur modifié avec succès ! Redirection...
                        </Alert>
                    )}

                    {submitError && (
                        <Alert variant="danger" onClose={() => setSubmitError('')} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {submitError}
                        </Alert>
                    )}

                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                            <h5 className="mb-0 text-white">
                                <i className="ri-edit-line me-2"></i>
                                Modifier le fournisseur
                            </h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                                            <i className="ri-information-line me-2"></i>
                                            Informations générales
                                        </h6>
                                        
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Raison sociale <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="raison_sociale"
                                                value={formData.raison_sociale || ''}
                                                onChange={handleChange}
                                                isInvalid={!!errors.raison_sociale}
                                                required
                                                className="border-2 rounded-3"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.raison_sociale}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Code fournisseur</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={formData.code_fournisseur || ''}
                                                disabled
                                                readOnly
                                                className="bg-light border-2 rounded-3"
                                            />
                                            <Form.Text className="text-muted small">
                                                Le code fournisseur n'est pas modifiable
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                                            <i className="ri-phone-line me-2"></i>
                                            Coordonnées
                                        </h6>
                                        
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Téléphone</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="telephone"
                                                value={formData.telephone || ''}
                                                onChange={handleChange}
                                                className="border-2 rounded-3"
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Email</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email || ''}
                                                onChange={handleChange}
                                                className="border-2 rounded-3"
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Adresse</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                name="adresse"
                                                value={formData.adresse || ''}
                                                onChange={handleChange}
                                                rows={2}
                                                className="border-2 rounded-3"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="mt-4">
                                    <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                                        <i className="ri-briefcase-line me-2"></i>
                                        Identifiants juridiques
                                    </h6>
                                    
                                    <Row className="g-3">
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold">ICE</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="ice"
                                                    value={formData.ice || ''}
                                                    onChange={handleChange}
                                                    className="border-2 rounded-3"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold">Patente</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="patente"
                                                    value={formData.patente || ''}
                                                    onChange={handleChange}
                                                    className="border-2 rounded-3"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold">RC</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="rc"
                                                    value={formData.rc || ''}
                                                    onChange={handleChange}
                                                    className="border-2 rounded-3"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold">Identifiant Fiscal (IF)</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="identifiant_fiscal"
                                                    value={formData.identifiant_fiscal || ''}
                                                    onChange={handleChange}
                                                    className="border-2 rounded-3"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-semibold">CNSS</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="cnss"
                                                    value={formData.cnss || ''}
                                                    onChange={handleChange}
                                                    className="border-2 rounded-3"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>

                                <Row className="mt-3">
                                    <Col md={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Conditions de règlement</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                name="conditions_reglement"
                                                value={formData.conditions_reglement || ''}
                                                onChange={handleChange}
                                                rows={3}
                                                placeholder="Ex: Paiement à 30 jours, escompte de 2%..."
                                                className="border-2 rounded-3"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end gap-2 mt-4 pt-2">
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => navigate('/achats/fournisseurs')}
                                        className="rounded-pill px-4"
                                    >
                                        Annuler
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        variant="primary" 
                                        disabled={saving}
                                        className="rounded-pill px-5"
                                        style={{ background: '#21ACCD', border: 'none' }}
                                    >
                                        {saving ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Enregistrement...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-save-line me-2"></i>
                                                Enregistrer
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
            `}</style>
        </>
    );
}